/**
 * G4 — Solve Celebration (G3 Result in v2.1).
 * 1:1 with assets/v2.0/from-claude-design/G3 Result v2.0.html.
 *
 * P-2.2 (MASTERPIECE-v2.2 §2.2):
 *   - Keep:  1 percentile indicator + 1 opt-in share icon + 1 countdown + rarity badge
 *   - Cut:   multi-stat block (Time / Attempts / Streak) — "優雅 over 完整"
 *   - Share: demoted to small opt-in icon (not primary CTA; no reward framing)
 *   - Countdown: driven by worker UTC time (P-2.2.3) to prevent local-clock gaming
 *
 * M11 — Solve Stillness:
 *   T=0-800ms     gold ripple + haptic (play-engine fires onWin)
 *   T=800-2400ms  static — graph + copy visible, NO CTAs
 *   T=2400ms      CTAs fade in: small share icon · Practice · Done
 *
 * Conditional copy variants (4):
 *   first_solve — first time ever solving
 *   beat95      — beat top 5% percentile (totalSolves >= 10 gate)
 *   beat80      — beat top 20%
 *   neutral     — default
 *
 * Percentile hidden until totalSolves >= 10 (anti-sandbagging).
 */
import { generateDailyPuzzle } from '../../engine/generate';
import { tierFor } from '../../engine/tier';
import { fmtTime, t } from '../../i18n/runtime';
import { profile } from '../../state/profile';
import { getServerTime } from '../../state/sync';
import { el, html, on } from '../html';
import { isMilestone, milestoneSeal } from '../milestone';
import type { RouteContext } from '../router';

/**
 * Inline SVG rarity glyphs — same paths as G2 play screen to keep the
 * brand language consistent across the play→solve flow. Unicode chars
 * (●◆▲★✦) are deliberately avoided per the iOS WKWebView font-fallback
 * issue that caused the cryptic "U" report in Builds 11-13.
 */
function rarityGlyphSvg(rarity: string): string {
  const paths: Record<string, string> = {
    common:    `<circle cx="8" cy="8" r="5" fill="currentColor"/>`,
    uncommon:  `<path d="M8 2 L14 8 L8 14 L2 8 Z" fill="currentColor"/>`,
    rare:      `<path d="M8 2 L14 13 L2 13 Z" fill="currentColor"/>`,
    epic:      `<path d="M8 2 L9.8 6.2 L14.4 6.5 L10.8 9.5 L12 14 L8 11.5 L4 14 L5.2 9.5 L1.6 6.5 L6.2 6.2 Z" fill="currentColor"/>`,
    legendary: `<path d="M8 1.5 L9.4 5.8 L13.8 5 L11.2 8.5 L13.8 12 L9.4 11.2 L8 15.5 L6.6 11.2 L2.2 12 L4.8 8.5 L2.2 5 L6.6 5.8 Z" fill="currentColor"/>`,
  };
  return `<svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true" focusable="false">${paths[rarity] ?? paths['common']}</svg>`;
}
/** i18n keys — labels render in the user's locale (no hardcoded English) */
const RARITY_LABEL_KEY: Record<string, string> = {
  common: 'rarity.common', uncommon: 'rarity.uncommon',
  rare: 'rarity.rare', epic: 'rarity.epic', legendary: 'rarity.legendary',
};

/** M11 — Solve Stillness: 1600ms static pause before CTAs appear. */
const STILLNESS_DELAY = 2400; // ms from win: 800ms ripple + 1600ms still

/** Pick conditional header copy for result screen (4 variants). */
function resultCopy(p: ReturnType<ReturnType<typeof profile>['get']>, percentile: number | null): string {
  if (p.totalSolves === 1) return t('g3.result.firstSolve');
  const showPercentile = p.totalSolves >= 10 && percentile !== null;
  if (showPercentile && percentile! >= 0.95) return t('g3.result.beat95', { n: Math.round(percentile! * 100) });
  if (showPercentile && percentile! >= 0.80) return t('g3.result.beat80', { n: Math.round(percentile! * 100) });
  return t('g3.result.neutral');
}

export default function (ctx: RouteContext): { node: HTMLElement; cleanup: () => void } {
  const date = ctx.params['date'] ?? '';
  // percentile passed from play screen via query param (0-1 float); absent = null
  const percentileRaw = ctx.query.get('pct');
  const percentile = percentileRaw !== null ? parseFloat(percentileRaw) : null;
  // Solve time + attempts are passed from the play screen (?t=ms&a=n). These drive
  // the quiet reward lines (time / percentile / one-pass), shown without breaking
  // the austere "elegant over complete" tone — single mono values, no scoreboard.
  const timeMsRaw = ctx.query.get('t');
  const timeMs = timeMsRaw !== null ? parseInt(timeMsRaw, 10) : null;
  const attemptsRaw = ctx.query.get('a');
  const attempts = attemptsRaw !== null ? parseInt(attemptsRaw, 10) : null;
  // "Perfect" = solved with no wrong moves (attempts === 1). A whisper, not a badge.
  const isPerfect = attempts === 1;
  const puzzle = generateDailyPuzzle(date);
  const p = profile().get();
  const copy = resultCopy(p, percentile);
  // Percentile line: unlocked at >=10 solves (anti-sandbagging, I-12). Before that,
  // a faint "unlocks in N" so the player knows it's coming (never a fake number).
  const solvesToUnlock = Math.max(0, 10 - p.totalSolves);
  const pctUnlocked = p.totalSolves >= 10 && percentile !== null;
  // The celebratory headline (resultCopy) already announces the percentile when it
  // is a beat80/beat95 variant — don't repeat the number in the stat row too.
  const headlineHasPct = pctUnlocked && percentile! >= 0.8;
  // Build the quiet reward rows (reuses the screen's own austere .solve-stats CSS).
  const statRows: string[] = [];
  if (timeMs !== null) {
    statRows.push(
      `<div class="solve-stat"><span class="label">${t('solve.stat_time')}</span><span class="value">${fmtTime(timeMs / 1000)}</span></div>`,
    );
  }
  if (!headlineHasPct) {
    statRows.push(
      pctUnlocked
        ? `<div class="solve-stat"><span class="label">${t('solve.stat_percentile')}</span><span class="value value-gold">${t('solve.faster_than', { n: Math.round(percentile! * 100) })}</span></div>`
        : `<div class="solve-stat"><span class="label">${t('solve.stat_percentile')}</span><span class="value value-muted">${t('solve.pct_locked', { n: solvesToUnlock })}</span></div>`,
    );
  }
  if (isPerfect) {
    statRows.push(
      `<div class="solve-stat solve-stat-perfect"><span class="label">${t('solve.stat_pass')}</span><span class="value value-gold">${t('solve.one_pass')}</span></div>`,
    );
  }
  // FDT-O-010 — the player's tier (檔次). Rating updates server-side per Daily
  // solve (mirrored into profile.rating by g2); tierFor is the shared source.
  const tier = tierFor(p.rating);
  // FDT-O-001 AC-3 — after the very first daily solve (totalSolves===1, since the
  // welcome warm-up never records a solve), the Practice CTA softly nudges
  // "want a few more?" toward the new chapter ladder (Ch1 is already unlocked).
  const isFirstSolve = p.totalSolves === 1;
  const practiceCta = isFirstSolve ? t('g3.result.ctaPracticeFirst') : t('g3.result.ctaPractice');

  const reduceMotion =
    p.a11y?.reduceMotion === 'on' ||
    (p.a11y?.reduceMotion !== 'off' && matchMedia('(prefers-reduced-motion: reduce)').matches);

  const root = el(html`
    <section class="screen-solve" data-rarity="${puzzle.rarity}" aria-label="${t('solve.title')}">
      <div class="solve-backdrop-veil" aria-hidden="true"></div>
      <div class="solve-card-wrap">
        <div class="solve-card">
          <div class="solve-check" aria-hidden="true">
            <svg viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="14" stroke="var(--rarity,currentColor)" stroke-width="1.6" fill="none"/>
              <path d="M10 16.5 L14 20.5 L22 12.5" stroke="var(--rarity,currentColor)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p class="solve-copy" aria-live="polite">${copy}</p>
          <h2 class="solve-heading">${t('solve.title')}</h2>
          <div class="solve-rarity rarity-${puzzle.rarity}">
            <span class="solve-rarity-symbol" aria-hidden="true">${rarityGlyphSvg(puzzle.rarity)}</span>
            <span class="solve-rarity-label">${t(RARITY_LABEL_KEY[puzzle.rarity] ?? 'rarity.common')}</span>
          </div>
          <!-- FDT-O-010 — player tier (檔次): your standing, not the puzzle's rarity -->
          <div class="solve-tier">
            <span class="solve-tier-lbl">${t('solve.your_tier')}</span>
            <span class="solve-tier-badge"><span class="solve-tier-glyph" aria-hidden="true">${tier.glyph}</span> ${t(tier.key)}</span>
          </div>
          <!-- Quiet reward lines (austere .solve-stats): time · percentile · one-pass.
               Fade in shortly after the copy; well before the CTAs (Solve Stillness). -->
          <div class="solve-stats">${statRows.join('')}</div>
          <!-- A whisper-quiet link to replay / watch the canonical solution. -->
          <button class="solve-watch-link" data-watch>${t('solve.watch_solution')}</button>
          <!-- M11 Solve Stillness: CTAs hidden until STILLNESS_DELAY ms -->
          <div class="solve-actions" data-actions style="opacity:0;pointer-events:none" aria-hidden="true">
            ${isFirstSolve
              ? html`<div class="solve-actions-stack">
                  <!-- FDT-O-001 AC-3: the first solve makes Practice the primary,
                       full-width CTA — the longer "want a few more?" copy never
                       wraps (the 3-up row's buttons are only ~66px), and onboarding
                       points clearly at the new chapter ladder. -->
                  <button class="solve-btn solve-btn-primary" data-practice>${practiceCta}</button>
                  <button class="solve-btn solve-btn-secondary" data-next>${t('g3.result.ctaDone')}</button>
                </div>`
              : html`<div class="solve-actions-row">
                  <button class="solve-btn solve-btn-practice" data-practice>${practiceCta}</button>
                  <button class="solve-btn solve-btn-done" data-next>${t('g3.result.ctaDone')}</button>
                  <!-- P-2.2.2: share is small opt-in icon, not primary CTA -->
                  <button class="solve-share-icon" data-share aria-label="${t('g3.result.ctaShare')}" title="${t('g3.result.ctaShare')}">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 12 V20 A2 2 0 0 0 6 22 H18 A2 2 0 0 0 20 20 V12 M16 6 L12 2 L8 6 M12 2 V15"
                        stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>`}
          </div>
        </div>
      </div>
      <p class="solve-countdown" data-countdown></p>
    </section>
  `);
  // Apply rarity color via CSS variable on the host so .rarity-* tint propagates.
  root.style.setProperty('--rarity', `var(--rarity-${puzzle.rarity})`);

  const actionsEl = root.querySelector('[data-actions]') as HTMLElement;

  // M11 — Solve Stillness: reveal CTAs after delay (reduced motion: instant)
  const delay = reduceMotion ? 0 : STILLNESS_DELAY;
  const stillnessTimer = setTimeout(() => {
    actionsEl.style.opacity = '1';
    actionsEl.style.pointerEvents = '';
    actionsEl.removeAttribute('aria-hidden');
    if (!reduceMotion) {
      actionsEl.style.transition = 'opacity 400ms ease';
    }
  }, delay);

  on(root, 'click', '[data-share]', () => ctx.navigate(`/share/${date}`));
  on(root, 'click', '[data-practice]', () => ctx.navigate('/practice'));
  on(root, 'click', '[data-next]', () => ctx.navigate('/'));
  // Watch / replay the canonical solution for this puzzle.
  on(root, 'click', '[data-watch]', () => ctx.navigate(`/review/${date}`));

  // P-2.2.3 — Countdown driven by worker UTC time (local fallback).
  const cd = root.querySelector('[data-countdown]') as HTMLElement;
  let serverOffset = 0; // ms difference between server and local clock (populated async)

  const renderCountdown = (nowMs: number) => {
    const now = nowMs + serverOffset;
    // Next UTC midnight
    const tomorrow = Math.ceil((now + 1) / 86400000) * 86400000;
    const ms = tomorrow - now;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    cd.textContent = t('solve.countdown', { h, m });
  };

  renderCountdown(Date.now());
  const countdownHandle = setInterval(() => renderCountdown(Date.now()), 60000);

  // Async: fetch server time to calibrate offset, then re-render.
  getServerTime().then((serverMs) => {
    serverOffset = serverMs - Date.now();
    renderCountdown(Date.now());
  });

  // FDT-O-004 — streak milestone seal (7/30/100); only on today's fresh solve
  // (not a re-visit of an old /solve). Appears after the ripple, auto-dismisses.
  let msOverlay: HTMLElement | null = null;
  let msTimer: ReturnType<typeof setTimeout> | null = null;
  if (isMilestone(p.streak) && p.lastSolveDate === date) {
    msTimer = setTimeout(() => {
      msOverlay = milestoneSeal(p.streak, { reduceMotion, onDismiss: () => { msOverlay = null; } });
      root.appendChild(msOverlay);
    }, reduceMotion ? 200 : 500);
  }

  return {
    node: root,
    cleanup() {
      clearTimeout(stillnessTimer);
      clearInterval(countdownHandle);
      if (msTimer) clearTimeout(msTimer);
      msOverlay?.remove();
    },
  };
}
